import { kernelsClient } from "@kaggle/sdk";
import { AnyAction } from "redux";
import { EditorStatus } from "../../types/EditorStatus";
import { createRingBuffer } from "../../utils/createRingBuffer";
import { BatchedRunActionKey, NotebookActionKey } from "../actions/ActionKeys";
import { JupyterActionKey } from "../actions/jupyter";
import {
  getCurrentEditorStatus,
  getCurrentKernelRunId,
} from "../selectors/current";
import { getJupyterStatus, isJupyterConnected } from "../selectors/jupyter";
import { getIsKernelRunning } from "../selectors/status";
import { AppMiddleware, JupyterStatus } from "../types";

/** Allow no more than one keepalive every 60 seconds. */
const MAX_KEEPALIVE_FREQUENCY_MS = 60 * 1000;

/** Ignore actions within 5s of initialization. */
const IGNORE_INIT_ACTION_MS = 5 * 1000;

/** The number of results to track for analytics. */
const MAX_RECENT_RESULTS_COUNT = 20;

/** Redux actions which indicate user activity. */
const USER_INITIATED_ACTIONS = [
  JupyterActionKey.SET_JUPYTER_STATE,
  BatchedRunActionKey.SET_BATCHED_RUN_STATUS,
  NotebookActionKey.SET_IPYNB,
];

/** Tracks reasons we skip keep alives vs. send them. */
enum KeepAliveOutcome {
  SEND_KEEPALIVE = "SEND_KEEPALIVE",
  REJECT_FREQUENCY_THROTTLE = "REJECT_FREQUENCY_THROTTLE",
  REJECT_JUPYTER_NOT_RUNNING = "REJECT_JUPYTER_NOT_RUNNING",
  REJECT_EDITOR_NOT_RUNNING = "REJECT_EDITOR_NOT_RUNNING",
  REJECT_INIT_THROTTLE = "REJECT_INIT_THROTTLE",
  REJECT_KERNEL_IDLE_NO_INTERACTION = "REJECT_KERNEL_IDLE_NO_INTERACTION",
  REJECT_NO_PROXY_URL = "REJECT_NO_PROXY_URL",
}

/** Create a ring buffer to track recent outcomes. Stores tuples (result, timestamp). */
const recentOutcomesBuffer = createRingBuffer<[KeepAliveOutcome, number]>(
  MAX_RECENT_RESULTS_COUNT
);

/** Get the list of recent results. */
export const getRecentKeepAliveOutcomes = () => recentOutcomesBuffer.toArray();

// HACK(cochamberlain): Temporarily adding this onto window so we can validate in prod (potentially useful in debugging short term).
(window as any).getRecentKeepAliveOutcomes = getRecentKeepAliveOutcomes;

let lastKeepAliveMS = 0;
let notebookInitMS: number | null = null;

/*
 * Send a keepalive whenever a user-triggered redux action occurs,
 * no more than once every 60 seconds.
 */
export const keepAliveMiddleware: AppMiddleware =
  ({ getState }) =>
  next =>
  (action: AnyAction) => {
    // Let reducers run then process keep-alive on middleware unwind.
    const result = next(action);

    // Get the current epoch time and state snapshot for use throughout middleware.
    const now = Date.now();
    const state = getState();

    /** Determines whether a keep-alive should be sent. */
    const getKeepAliveOutcome = (): KeepAliveOutcome => {
      // Send max of one keep-alive per minute.
      if (now - lastKeepAliveMS < MAX_KEEPALIVE_FREQUENCY_MS) {
        return KeepAliveOutcome.REJECT_FREQUENCY_THROTTLE;
      }

      const editorStatus = getCurrentEditorStatus(state);
      const isEditorRunning = editorStatus === EditorStatus.RUNNING;
      if (!isEditorRunning) {
        notebookInitMS = null;
        return KeepAliveOutcome.REJECT_EDITOR_NOT_RUNNING;
      }

      const jupyterStatus = getJupyterStatus(state);
      const isJupyterRunning =
        jupyterStatus === JupyterStatus.RUNNING ||
        jupyterStatus === JupyterStatus.CONNECTED;

      // If Jupyter isn't running right now, clear the session init time and bypass keepalive.
      if (!isJupyterRunning) {
        notebookInitMS = null;
        return KeepAliveOutcome.REJECT_JUPYTER_NOT_RUNNING;
      }

      // First time this middleware is hit after a session start. Jupyter is running so set init time to now.
      if (notebookInitMS === null) {
        notebookInitMS = now;
      }

      // Notebook initialization fires several actions that we need to ignore.
      const ignoreInitAction = now - notebookInitMS < IGNORE_INIT_ACTION_MS;
      if (ignoreInitAction) {
        return KeepAliveOutcome.REJECT_INIT_THROTTLE;
      }

      const isKernelIdle = !getIsKernelRunning(state);
      const isActionUserInitiated = USER_INITIATED_ACTIONS.includes(
        action.type
      );

      // If interactive kernel isn't busy and the current action didn't come from user interaction, ignore it.
      if (isKernelIdle && !isActionUserInitiated) {
        return KeepAliveOutcome.REJECT_KERNEL_IDLE_NO_INTERACTION;
      }

      if (!isJupyterConnected(state)) {
        return KeepAliveOutcome.REJECT_NO_PROXY_URL;
      }

      // No conditions preventing keep-alive.
      return KeepAliveOutcome.SEND_KEEPALIVE;
    };

    const outcome = getKeepAliveOutcome();

    // Track the last N outcomes / timestamps for debugging.
    recentOutcomesBuffer.push([outcome, now]);

    if (outcome !== KeepAliveOutcome.SEND_KEEPALIVE) {
      return result;
    }

    lastKeepAliveMS = now;

    const kernelRunId = getCurrentKernelRunId(state);

    if (kernelRunId) {
      // Fire and forget the keep-alive.
      kernelsClient.kernelsService
        .updateKernelSessionKeepalive({
          kernelSessionId: kernelRunId,
        })
        // tslint:disable-next-line: no-console
        .catch(console.error);
    }

    return result;
  };
